import React, { useEffect, useRef } from "react"
import blogClient from "../clients/blog"

const TWO_PI = Math.PI * 2

// neon subject colours, handed out in the order subjects first appear
const SUBJECT_COLORS = [
  "#00e5ff",
  "#ff3d81",
  "#7cff4d",
  "#ffb020",
  "#b06bff",
  "#00ffa3",
  "#ff5a3c",
  "#3d9bff",
  "#ff5ada",
  "#d4ff3d",
  "#ff8f2e",
  "#28d8c8",
  "#c8ff88",
]

// wide + short: the torus's theta circumference is ~5x its tube, so the unrolled
// map is a broad strip, and this keeps the hex cells close to regular
const W = 264
const H = 78

export const MiniMap = ({ torusRef }) => {
  const canvasRef = useRef()

  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const dpr = window.devicePixelRatio || 1
    cv.width = W * dpr
    cv.height = H * dpr
    const ctx = cv.getContext("2d")
    ctx.scale(dpr, dpr)

    // grid dimensions come from the feed's layout; defaults until it loads
    let COLS = 32
    let ROWS = 26
    let THETA_I, PHI_I, cellW, cellH, rx, ry
    let phiForRow = [] // camera positionPhi that centres each row (non-linear)
    const wrap2pi = v => ((v % TWO_PI) + TWO_PI) % TWO_PI
    const mod2 = (v, m) => ((v % m) + m) % m
    const recompute = () => {
      THETA_I = TWO_PI / (3 * COLS)
      PHI_I = TWO_PI / ROWS
      cellW = W / COLS
      cellH = H / ROWS
      // non-regular hexes tuned to the grid's real aspect so they tessellate:
      // a cell touches (row +/-2, col) flat-to-flat and (row +/-1, col / col-1)
      // on the slant. 0.94 leaves a hairline gap, matching the torus.
      rx = (0.94 * cellW) / 3
      ry = (0.94 * cellH) / Math.sin(Math.PI / 3)
      // inverse of the "aim camera at tile" math used elsewhere:
      //   positionPhi = atan2(4 sin p, 1 + 4 cos p) + PI   (r = 4 tube radius)
      phiForRow = []
      for (let k = 0; k < ROWS; k++) {
        const p = k * PHI_I
        phiForRow.push(
          wrap2pi(Math.atan2(4 * Math.sin(p), 1 + 4 * Math.cos(p)) + Math.PI)
        )
      }
    }
    recompute()

    // where (fractional row/col) the 3D camera is currently looking
    const cameraCentre = t => {
      // theta is linear: positionTheta = tileTheta - PI/2
      const th = wrap2pi(t.positionTheta + Math.PI / 2)
      const colFrac = (th / TWO_PI) * COLS
      // phi is non-linear: find the row bracket around positionPhi and lerp
      const pp = wrap2pi(t.positionPhi)
      let best = 0
      let bestD = Infinity
      for (let k = 0; k < ROWS; k++) {
        let d = Math.abs(pp - phiForRow[k])
        d = Math.min(d, TWO_PI - d)
        if (d < bestD) (bestD = d), (best = k)
      }
      const step = (a, b) => {
        let d = b - a
        if (d > Math.PI) d -= TWO_PI
        if (d < -Math.PI) d += TWO_PI
        return d
      }
      const dNext = step(phiForRow[best], phiForRow[(best + 1) % ROWS])
      const dPrev = step(phiForRow[(best - 1 + ROWS) % ROWS], phiForRow[best])
      let rowFrac = best
      const off = step(phiForRow[best], pp)
      if (off >= 0 && Math.abs(dNext) > 1e-6) rowFrac = best + off / dNext
      else if (off < 0 && Math.abs(dPrev) > 1e-6) rowFrac = best + off / dPrev
      return { colFrac, rowFrac: ((rowFrac % ROWS) + ROWS) % ROWS }
    }

    // points left/right, flat top/bottom - same orientation as the torus hexes
    const hexAt = (g, cx, cy) => {
      g.beginPath()
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k
        const px = cx + rx * Math.cos(a)
        const py = cy + ry * Math.sin(a)
        k ? g.lineTo(px, py) : g.moveTo(px, py)
      }
      g.closePath()
    }
    // x mirrored so the map reads the same way round as the 3D view
    const centre = (r, c) => [
      W - ((c + (r % 2 ? 0.5 : 0)) * cellW + cellW / 2),
      r * cellH + cellH / 2,
    ]

    let raf
    let bg = null // pre-rendered full map (exactly W x H), scrolled each frame

    const cellOf = pos => {
      const r = ((Math.round(pos.y / PHI_I) % ROWS) + ROWS) % ROWS
      const off = r % 2 === 1 ? 1.5 * THETA_I : 0
      const c = ((Math.round((pos.x - off) / (3 * THETA_I)) % COLS) + COLS) % COLS
      return [r, c]
    }

    blogClient.Post.list().then(res => {
      const items = res.data().items
      const layout = res.data().layout || {}
      if (layout.THETA_POSITIONS) COLS = layout.THETA_POSITIONS
      if (layout.PHI_POSITIONS) ROWS = layout.PHI_POSITIONS
      recompute()

      // primary subject tag per item = its most feed-frequent tag (matches the
      // layout in serializeFeed.js), then a distinct hue per subject in the
      // order the subjects first appear along the theta ring.
      const freq = {}
      items.forEach(it =>
        (it.tags || []).forEach(t => {
          t = String(t).toLowerCase()
          freq[t] = (freq[t] || 0) + 1
        })
      )
      // NB: this bundle's babel doesn't down-level spread of Set/Map iterators,
      // so dedupe by hand and never `[...set]`.
      const primaryOf = it => {
        const raw = (it.tags || []).map(t => String(t).toLowerCase())
        const ts = raw.filter((v, i) => raw.indexOf(v) === i)
        if (!ts.length) return "~untagged"
        ts.sort((a, b) => freq[b] - freq[a] || (a < b ? -1 : 1))
        return ts[0]
      }
      const subjects = []
      items.forEach(it => {
        const p = primaryOf(it)
        if (subjects.indexOf(p) === -1) subjects.push(p)
      })
      const colorOf = {}
      subjects.forEach((s, i) => {
        colorOf[s] = SUBJECT_COLORS[i % SUBJECT_COLORS.length]
      })

      const occ = {}
      items.forEach(it => {
        const [r, c] = cellOf(it.position)
        occ[r + "," + c] = colorOf[primaryOf(it)]
      })

      // static layer, on solid dark so the neon colours read true. Rows/cols run
      // a couple past the edges with wrapped lookup so the tiled blit stitches
      // seamlessly across both torus seams.
      bg = document.createElement("canvas")
      bg.width = cv.width
      bg.height = cv.height
      const bx = bg.getContext("2d")
      bx.scale(dpr, dpr)
      bx.lineJoin = "round"
      bx.fillStyle = "#05090f"
      bx.fillRect(0, 0, W, H)
      const mod = (n, m) => ((n % m) + m) % m
      // one full period plus a one-cell margin (wrapped lookup) so a hex
      // straddling a seam is drawn whole; the 3x3 blit in draw() lines the
      // tiles up on top of that.
      for (let r = -1; r <= ROWS; r++) {
        for (let c = -1; c <= COLS; c++) {
          const color = occ[mod(r, ROWS) + "," + mod(c, COLS)]
          const [cx, cy] = centre(r, c)
          hexAt(bx, cx, cy)
          if (color === undefined) {
            bx.lineWidth = 1
            bx.strokeStyle = "rgba(110, 225, 255, 0.28)"
            bx.stroke()
          } else {
            bx.fillStyle = color
            bx.shadowColor = color
            bx.shadowBlur = 3
            bx.fill()
            bx.shadowBlur = 0
            bx.lineWidth = 0.8
            bx.strokeStyle = "rgba(255,255,255,0.85)"
            bx.stroke()
          }
        }
      }
    })

    const draw = () => {
      raf = requestAnimationFrame(draw)
      ctx.clearRect(0, 0, W, H)

      const t = torusRef.current
      let cf = 0
      let rf = 0
      if (t) {
        const cc = cameraCentre(t)
        cf = cc.colFrac
        rf = cc.rowFrac
      }

      if (bg) {
        // pan so the looked-at cell sits at the panel centre (no brick term ->
        // smooth, no left/right snapping as the row parity flips)
        // centre() mirrors x, so cell cf sits at px = W - (cf + 0.5) * cellW
        const px = W - (cf + 0.5) * cellW
        const py = (rf + 0.5) * cellH
        // keep the pan within one period; blit 3x3 with a 1px overlap so the
        // seams between tiles never show a sub-pixel gap
        const panX = mod2(W / 2 - px, W)
        const panY = mod2(H / 2 - py, H)
        for (let j = -1; j <= 1; j++)
          for (let i = -1; i <= 1; i++)
            ctx.drawImage(bg, panX + i * W - 0.5, panY + j * H - 0.5, W + 1, H + 1)
      }

      // fixed "you are here" box at the centre of the panel
      ctx.save()
      ctx.strokeStyle = "rgba(255,255,255,0.95)"
      ctx.lineWidth = 1.4
      ctx.shadowColor = "rgba(120,240,255,0.9)"
      ctx.shadowBlur = 6
      const bw = cellW * 3
      const bh = cellH * 5
      ctx.strokeRect(W / 2 - bw / 2, H / 2 - bh / 2, bw, bh)
      ctx.restore()
    }
    draw()
    return () => cancelAnimationFrame(raf)
  }, [torusRef])

  return (
    <div
      style={{
        position: "fixed",
        left: 12,
        bottom: 12,
        zIndex: 1001,
        padding: 6,
        background: "rgba(3, 9, 15, 0.9)",
        border: "1px solid rgba(90, 220, 255, 0.5)",
        borderRadius: 3,
        boxShadow: "0 0 14px rgba(60, 210, 255, 0.25)",
        backdropFilter: "blur(1px)",
        pointerEvents: "none",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: W, height: H }}
      />
    </div>
  )
}
